/**
 * ProductImagesHeader - Header bar for ProductImagesSubTab
 *
 * Shows title, image count, size toggle, sync/optimize/library buttons.
 */

import { CloudDownload, Sparkles, Images, Settings } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/v2/ui'
import type { ProductAsset } from '@/types/products'
import type { ImageSize } from './types'

interface ProductImagesHeaderProps {
  assets: ProductAsset[]
  imageSize: ImageSize
  setImageSize: (size: ImageSize) => void
  syncing: boolean
  saving: boolean
  optimizing: boolean
  isLibraryOpen: boolean
  onSyncAll: () => void
  onViewSettings: (channelId?: string, channelName?: string) => void
  onOptimizeImages: (channelId?: string) => void
  onToggleLibrary: () => void
}

export function ProductImagesHeader({
  assets, imageSize, setImageSize, syncing, saving, optimizing,
  isLibraryOpen, onSyncAll, onViewSettings, onOptimizeImages, onToggleLibrary,
}: ProductImagesHeaderProps) {
  const globalCount = assets.filter((a) => !a.channel_id).length
  const channelCount = assets.filter((a) => a.channel_id).length

  return (
    <div className="flex justify-between items-center mb-6 flex-shrink-0">
      <TitleSection total={assets.length} globalCount={globalCount} channelCount={channelCount} />
      <ActionButtons
        imageSize={imageSize} setImageSize={setImageSize}
        syncing={syncing} saving={saving} optimizing={optimizing}
        isLibraryOpen={isLibraryOpen} hasAssets={assets.length > 0}
        onSyncAll={onSyncAll} onViewSettings={() => onViewSettings()}
        onOptimizeImages={() => onOptimizeImages()} onToggleLibrary={onToggleLibrary}
      />
    </div>
  )
}

function TitleSection({ total, globalCount, channelCount }: {
  total: number; globalCount: number; channelCount: number
}) {
  return (
    <div>
      <h6 className="text-lg font-semibold text-neutral-900 dark:text-neutral-50">
        Produktbilder
      </h6>
      <span className="text-sm text-neutral-600 dark:text-neutral-400">
        {total} Bild{total !== 1 ? 'er' : ''} gesamt
        {channelCount > 0 && (
          <> &bull; {globalCount} global, {channelCount} channel-spezifisch</>
        )}
      </span>
    </div>
  )
}

function ActionButtons(props: {
  imageSize: ImageSize; setImageSize: (s: ImageSize) => void
  syncing: boolean; saving: boolean; optimizing: boolean
  isLibraryOpen: boolean; hasAssets: boolean
  onSyncAll: () => void; onViewSettings: () => void
  onOptimizeImages: () => void; onToggleLibrary: () => void
}) {
  const disabled = props.syncing || props.saving || props.optimizing

  return (
    <div className="flex gap-2">
      <SizeToggle imageSize={props.imageSize} setImageSize={props.setImageSize} />
      <SyncButton syncing={props.syncing} disabled={disabled} onClick={props.onSyncAll} />
      <Button variant="ghost" onClick={props.onViewSettings} disabled={disabled} title="Einstellungen">
        <Settings className="h-4 w-4" />
      </Button>
      <OptimizeButton optimizing={props.optimizing} disabled={disabled || !props.hasAssets} onClick={props.onOptimizeImages} />
      <LibraryButton isOpen={props.isLibraryOpen} disabled={props.syncing || props.saving} onClick={props.onToggleLibrary} />
    </div>
  )
}

function SizeToggle({ imageSize, setImageSize }: { imageSize: ImageSize; setImageSize: (s: ImageSize) => void }) {
  return (
    <div className="flex rounded-lg border border-neutral-200 dark:border-neutral-700 p-1 gap-0.5">
      {(['S', 'M', 'L'] as const).map((size) => (
        <Button key={size} variant={imageSize === size ? 'primary' : 'ghost'} size="sm" onClick={() => setImageSize(size)}>
          {size}
        </Button>
      ))}
    </div>
  )
}

function SyncButton({ syncing, disabled, onClick }: { syncing: boolean; disabled: boolean; onClick: () => void }) {
  const { t } = useTranslation()
  return (
    <Button variant="outline" onClick={onClick} disabled={disabled}>
      {syncing ? (
        <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2" />Synchronisiere...</>
      ) : (
        <><CloudDownload className="h-4 w-4 mr-2" />{t('common.sync')}</>
      )}
    </Button>
  )
}

function OptimizeButton({ optimizing, disabled, onClick }: { optimizing: boolean; disabled: boolean; onClick: () => void }) {
  return (
    <Button variant="outline" onClick={onClick} disabled={disabled}>
      {optimizing ? (
        <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2" />Optimiere...</>
      ) : (
        <><Sparkles className="h-4 w-4 mr-2" />Bilder optimieren</>
      )}
    </Button>
  )
}

function LibraryButton({ isOpen, disabled, onClick }: { isOpen: boolean; disabled: boolean; onClick: () => void }) {
  return (
    <Button variant={isOpen ? 'primary' : 'outline'} onClick={onClick} disabled={disabled} className="gap-2">
      <Images className="h-4 w-4" />
      Library
      <kbd className="hidden sm:inline-flex px-1.5 py-0.5 text-2xs rounded bg-neutral-200 dark:bg-neutral-700 text-neutral-600 dark:text-neutral-400 font-mono ml-1">
        L
      </kbd>
    </Button>
  )
}
